import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {CenteredImage} from "@pg-design/centered-image";
import {borderRadius, calculateRemSize, listUnStyled, m, p, pr, truncate, w100} from "@pg-design/helpers-css-strings";
import {BrandCheckboxCheckedIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {IModularHousesPublicListOffer} from "@pg-mono/rp-api-routes";
import {rpAppLink} from "@pg-mono/rp-routes";
import {numberFormat} from "@pg-mono/string-utils";

import {OfferBoxBase, offerBoxImgStyle, offerBoxImgWrapperStyle} from "../../atoms/OfferBoxBase";
import {DesktopText} from "../../atoms/typography/DesktopText";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useResponsiveOpenLink} from "../../hooks/use_responsive_open_link";
import {PrimaryBadge} from "../../offer/components/offer_box/PrimaryBadge";
import {getCurrency} from "../../utils/get_currency";
import {getConstructionPermitText} from "../utils/get_construction_permit_text";
import {getConstructionTechText} from "../utils/get_construction_tech_text";
import {getConstructionTimeText} from "../utils/get_construction_time_text";

interface IProps {
    modularHouseOffer: IModularHousesPublicListOffer;
    className?: string;
    onClick?: () => void;
}

export function ModularHouseOfferBox(props: IProps) {
    const {modularHouseOffer, className, onClick} = props;
    const {name, id, image, construction_time, price_m2, contractor} = modularHouseOffer;
    const theme = useTheme();
    const target = useResponsiveLinkTarget();
    const offerLink = rpAppLink.modularHouseOffer.detail.base({contractorSlug: contractor.slug, offerSlug: convertToSlug(name), offerId: id.toString()});
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);
    const currency = getCurrency({});

    const onLinkClick = () => {
        onClick && onClick();
    };

    const onOfferClick = () => {
        onLinkClick();
        openResponsiveOfferLink();
    };

    const specList = getSpecList(modularHouseOffer);

    return (
        <OfferBoxBase className={className}>
            <a href={offerLink} title={name} target={target} css={offerBoxLinkStyle} onClick={onLinkClick}>
                <Image css={imgWrap} src={image || undefined} imageStyle={offerBoxImgStyle} alt={name} width="375px" height="211px" ratio={{xs: "375/211"}} />

                {construction_time && <PrimaryBadge css={badge}>{getConstructionTimeText(construction_time.toString())}</PrimaryBadge>}
            </a>
            <div css={offerBoxInfoHolder}>
                <div onClick={onOfferClick} css={offerBoxBaseInfoWrapperStyle}>
                    <DesktopText variant="headline_6" as="h2" css={offerNameStyle} className="bt">
                        {name}
                    </DesktopText>
                    {price_m2 ? (
                        <DesktopText variant="body_copy_2" as="div" strong>
                            <Highlight>
                                od{" "}
                                <span className="bt">
                                    {numberFormat(price_m2)} {currency}/m<sup>2</sup>
                                </span>
                            </Highlight>
                        </DesktopText>
                    ) : (
                        <NoTagPlug height="2rem" />
                    )}
                </div>
                <div css={specsAndLogoHolder}>
                    <div onClick={onOfferClick} css={specListWrapperStyle}>
                        <ul css={specListStyle}>
                            {specList.map((itemText, i) => (
                                <li key={i} css={listItem}>
                                    <BrandCheckboxCheckedIcon size="1.6" wrapperType="circle" wrapperSize="1.6" wrapperColor={theme.colors.primary} />
                                    <Text variant="info_txt_1">{itemText}</Text>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div css={imageWrap}>
                        <CenteredImage
                            css={imageHolder}
                            width={78}
                            height={58}
                            alt={`Logo producenta: ${modularHouseOffer.contractor.name}`}
                            src={modularHouseOffer.contractor.logo}
                        />
                    </div>
                </div>
                <div>
                    <Button css={ctaStyle} variant="filled_primary" href={offerLink} onClick={onLinkClick}>
                        Zobacz dom
                    </Button>
                </div>
            </div>
        </OfferBoxBase>
    );
}

// Utils
function convertToSlug(name: string): string {
    return name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
}

const getSpecList = (modularHouseOffer: IModularHousesPublicListOffer) => {
    const specList = ["Na Twojej działce"];

    if (modularHouseOffer.building_permit_required !== null) {
        specList.push(getConstructionPermitText(modularHouseOffer.building_permit_required));
    }

    if (modularHouseOffer.construction_technology) {
        specList.push(getConstructionTechText(modularHouseOffer.construction_technology));
    }

    return specList;
};

// Styles
export const offerBoxLinkStyle = css`
    display: flex;
    position: relative;
    overflow: hidden;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const imgWrap = css`
    ${borderRadius(2, 2, 0, 0)};
    cursor: pointer;
    ${offerBoxImgWrapperStyle};
`;

const INFO_PANEL_PADDING = calculateRemSize(1.5);

const badge = css`
    position: absolute;
    top: ${INFO_PANEL_PADDING};
    left: ${INFO_PANEL_PADDING};
`;

const offerBoxInfoHolder = css`
    ${p(2)};
    position: relative;
    min-height: 17.3rem;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const offerBoxBaseInfoWrapperStyle = css`
    cursor: pointer;
`;

const offerNameStyle = css`
    ${truncate};
`;

// We want to keep the layout of the box offer same at all times, despite the fact it may be missing some data:
const NoTagPlug = styled.div<{height: string; width?: string}>`
    ${({height, width}) => css`
        height: ${height};
        ${width && `width: ${width}`}
    `}
`;

const specsAndLogoHolder = css`
    display: flex;
    ${p(2, 0, 2, 0)};
    justify-content: space-between;
    align-items: center;
    min-height: 6rem;
    flex: 1 1 auto;
`;

const ctaStyle = (theme: Theme) => css`
    ${w100};
    //TODO fix this important shit
    text-decoration: none !important;
    color: ${theme.colors.secondary} !important;
`;

const specListWrapperStyle = css`
    ${pr()};
    cursor: pointer;
`;

const specListStyle = css`
    ${listUnStyled};
    ${m(0)};
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
`;

const listItem = css`
    display: flex;
    gap: ${calculateRemSize(0.5)};
    align-items: center;
`;

const imageWrap = css`
    flex: 1 1 auto;
    justify-content: flex-end;
    display: flex;
    align-items: center;
`;

const imageHolder = css`
    min-width: min-content;
    max-width: 78px;
    max-height: 58px;
`;
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {CenteredImage} from "@pg-design/centered-image";
import {borderRadius, calculateRemSize, listUnStyled, m, p, pr, truncate, w100} from "@pg-design/helpers-css-strings";
import {BrandCheckboxCheckedIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {IModularHousesPublicListOffer} from "@pg-mono/rp-api-routes";
import {rpAppLink} from "@pg-mono/rp-routes";
import {numberFormat} from "@pg-mono/string-utils";

import {OfferBoxBase, offerBoxImgStyle, offerBoxImgWrapperStyle} from "../../atoms/OfferBoxBase";
import {DesktopText} from "../../atoms/typography/DesktopText";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useResponsiveOpenLink} from "../../hooks/use_responsive_open_link";
import {PrimaryBadge} from "../../offer/components/offer_box/PrimaryBadge";
import {getCurrency} from "../../utils/get_currency";
import {getConstructionPermitText} from "../utils/get_construction_permit_text";
import {getConstructionTechText} from "../utils/get_construction_tech_text";
import {getConstructionTimeText} from "../utils/get_construction_time_text";

interface IProps {
    modularHouseOffer: IModularHousesPublicListOffer;
    className?: string;
    onClick?: () => void;
}

export function ModularHouseOfferBox(props: IProps) {
    const {modularHouseOffer, className, onClick} = props;
    const {name, id, image, construction_time, price_m2, contractor} = modularHouseOffer;
    const theme = useTheme();
    const target = useResponsiveLinkTarget();
    const offerLink = rpAppLink.modularHouseOffer.detail.base({contractorSlug: contractor.slug, offerSlug: convertToSlug(name), offerId: id.toString()});
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);
    const currency = getCurrency({});

    const onLinkClick = () => {
        onClick && onClick();
    };

    const onOfferClick = () => {
        onLinkClick();
        openResponsiveOfferLink();
    };

    const specList = getSpecList(modularHouseOffer);

    return (
        <OfferBoxBase className={className}>
            <a href={offerLink} title={name} target={target} css={offerBoxLinkStyle} onClick={onLinkClick}>
                <Image css={imgWrap} src={image || undefined} imageStyle={offerBoxImgStyle} alt={name} width="375px" height="211px" ratio={{xs: "375/211"}} />

                {construction_time && <PrimaryBadge css={badge}>{getConstructionTimeText(construction_time.toString())}</PrimaryBadge>}
            </a>
            <div css={offerBoxInfoHolder}>
                <div onClick={onOfferClick} css={offerBoxBaseInfoWrapperStyle}>
                    <DesktopText variant="headline_6" as="h2" css={offerNameStyle} className="bt">
                        {name}
                    </DesktopText>
                    {price_m2 ? (
                        <DesktopText variant="body_copy_2" as="div" strong>
                            <Highlight>
                                od{" "}
                                <span className="bt">
                                    {numberFormat(price_m2)} {currency}/m<sup>2</sup>
                                </span>
                            </Highlight>
                        </DesktopText>
                    ) : (
                        <NoTagPlug height="2rem" />
                    )}
                </div>
                <div css={specsAndLogoHolder}>
                    <div onClick={onOfferClick} css={specListWrapperStyle}>
                        <ul css={specListStyle}>
                            {specList.map((itemText, i) => (
                                <li key={i} css={listItem}>
                                    <BrandCheckboxCheckedIcon size="1.6" wrapperType="circle" wrapperSize="1.6" wrapperColor={theme.colors.primary} />
                                    <Text variant="info_txt_1">{itemText}</Text>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div css={imageWrap}>
                        <CenteredImage
                            css={imageHolder}
                            width={78}
                            height={58}
                            alt={`Logo producenta: ${modularHouseOffer.contractor.name}`}
                            src={modularHouseOffer.contractor.logo}
                        />
                    </div>
                </div>
                <div>
                    <Button css={ctaStyle} variant="filled_primary" href={offerLink} onClick={onLinkClick}>
                        Zobacz dom
                    </Button>
                </div>
            </div>
        </OfferBoxBase>
    );
}

// Utils
function convertToSlug(name: string): string {
    return name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
}

const getSpecList = (modularHouseOffer: IModularHousesPublicListOffer) => {
    const specList = ["Na Twojej działce"];

    if (modularHouseOffer.building_permit_required !== null) {
        specList.push(getConstructionPermitText(modularHouseOffer.building_permit_required));
    }

    if (modularHouseOffer.construction_technology) {
        specList.push(getConstructionTechText(modularHouseOffer.construction_technology));
    }

    return specList;
};

// Styles
export const offerBoxLinkStyle = css`
    display: flex;
    position: relative;
    overflow: hidden;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const imgWrap = css`
    ${borderRadius(2, 2, 0, 0)};
    cursor: pointer;
    ${offerBoxImgWrapperStyle};
`;

const INFO_PANEL_PADDING = calculateRemSize(1.5);

const badge = css`
    position: absolute;
    top: ${INFO_PANEL_PADDING};
    left: ${INFO_PANEL_PADDING};
`;

const offerBoxInfoHolder = css`
    ${p(2)};
    position: relative;
    min-height: 17.3rem;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const offerBoxBaseInfoWrapperStyle = css`
    cursor: pointer;
`;

const offerNameStyle = css`
    ${truncate};
`;

// We want to keep the layout of the box offer same at all times, despite the fact it may be missing some data:
const NoTagPlug = styled.div<{height: string; width?: string}>`
    ${({height, width}) => css`
        height: ${height};
        ${width && `width: ${width}`}
    `}
`;

const specsAndLogoHolder = css`
    display: flex;
    ${p(2, 0, 2, 0)};
    justify-content: space-between;
    align-items: center;
    min-height: 6rem;
    flex: 1 1 auto;
`;

const ctaStyle = (theme: Theme) => css`
    ${w100};
    //TODO fix this important shit
    text-decoration: none !important;
    color: ${theme.colors.secondary} !important;
`;

const specListWrapperStyle = css`
    ${pr()};
    cursor: pointer;
`;

const specListStyle = css`
    ${listUnStyled};
    ${m(0)};
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
`;

const listItem = css`
    display: flex;
    gap: ${calculateRemSize(0.5)};
    align-items: center;
`;

const imageWrap = css`
    flex: 1 1 auto;
    justify-content: flex-end;
    display: flex;
    align-items: center;
`;

const imageHolder = css`
    min-width: min-content;
    max-width: 78px;
    max-height: 58px;
`;
